<template>
	<NuxtLink
		:to="to"
		:class="['link-button', variantClasses[variant], sizeClasses]"
		:target="target"
		:rel="rel"
	>
		<slot>
			{{ text }}
		</slot>
	</NuxtLink>
</template>

<script setup lang="ts">
type LinkButtonVariant = 'primary' | 'outline' | 'text'
type LinkButtonSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const BASE_SIZE_CLASSES = {
	sm: 'text-sm px-3 py-1.5 min-h-9',
	md: 'text-base px-4 py-2 min-h-11',
	lg: 'text-lg px-5 py-2.5 min-h-12',
	xl: 'text-xl px-6 py-3 min-h-14',
	'2xl': 'text-2xl px-7 py-3.5 min-h-16'
}

const props = defineProps({
	to: {
		type: String,
		required: true
	},
	text: {
		type: String,
		default: 'Click Me'
	},
	variant: {
		type: String as PropType<LinkButtonVariant>,
		default: 'primary'
	},
	size: {
		type: String as PropType<LinkButtonSize>,
		default: 'md'
	},
	desktopSize: {
		type: String as PropType<LinkButtonSize | null>,
		default: null
	},
	target: {
		type: String,
		default: '_self'
	},
	rel: {
		type: String,
		default: ''
	}
})

const { size, desktopSize } = toRefs(props)

const variantClasses = computed<Record<LinkButtonVariant, string>>(() => {
	return {
		primary: 'link-button-primary',
		outline: 'link-button-outline',
		text: 'link-button-text'
	}
})

const desktopSizeClasses = computed(() => {
	return {
		sm: 'lg:text-sm lg:px-3 lg:py-1.5 lg:min-h-9',
		md: 'lg:text-base lg:px-4 lg:py-2 lg:min-h-11',
		lg: 'lg:text-lg lg:px-5 lg:py-2.5 lg:min-h-12',
		xl: 'lg:text-xl lg:px-6 lg:py-3 lg:min-h-14',
		'2xl': 'lg:text-2xl lg:px-7 lg:py-3.5 lg:min-h-16'
	}
})

const sizeClasses = computed(() => {
	return desktopSize.value
		? `${BASE_SIZE_CLASSES[size.value]} ${desktopSizeClasses.value[desktopSize.value]}`
		: BASE_SIZE_CLASSES[size.value]
})
</script>

<style scoped>
.link-button {
	@apply
		inline-flex
		items-center
		justify-center
		font-medium
		text-center
		transition-all
		duration-100
		ease-in-out
		rounded-lg
		focus:outline-none
		focus:ring-2
		focus:ring-offset-2
		focus:ring-mx-orange
		dark:ring-offset-mx-green-700;
}

.link-button-primary {
	@apply
		text-white
		bg-mx-orange
		hover:bg-mx-orange/80
		dark:bg-mx-orange/80
		dark:hover:bg-mx-orange
		hover:text-white
		shadow-sm;
}

.link-button-outline {
	@apply
		text-mx-orange
		border-2
		border-mx-orange
		hover:underline-offset-2
		hover:underline
		decoration-mx-orange
		decoration-2;
}

.link-button-text {
	@apply
		hover:underline-offset-2
		hover:underline
		decoration-mx-orange
		decoration-2
		transition-opacity;
}
</style>
